import type { QuestDef } from '../../game/quests';
import {
  arrive,
  collect,
  flag,
  interact,
  items,
  kill,
  reach,
  seeds,
  side,
  sideRewards,
  step,
  talk,
} from './kit';

/**
 * SIDE QUESTS. Each field zone has 9 (including a 2–3 quest chain and a
 * Glimmerseed/Heart pair: finding seeds reveals the zone's remaining ones on
 * the map, see `COLLECTIBLE_HINTS`), Havenreach has system guides (bounties,
 * cards → gems, sigils, bonds, herbs) and the isles have one each.
 */

// ================================================================ EMBERWILD VALE (Lv 3–9)
const EMBERWILD: QuestDef[] = [
  side({
    id: 'sq_ew_howl',
    name: 'Howls at Dusk',
    level: 3,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    requires: ['mq_002'],
    steps: [
      step(
        undefined,
        kill('vale_wolf', 8, 'Thin the pack by the river bend', 'emberwild', -70, 22),
      ),
    ],
    offer: [
      "The pack by the river bend has circled the camp every night since the Aetherfall. My sentries haven't slept in three days.",
      'Thin them out. Eight should teach the rest some manners.',
    ],
    complete: [
      'A quiet night ahead, for once. My sentries send their thanks. Well, one of them does. The others are already asleep.',
    ],
    rewards: sideRewards(3, 0.3, { items: items(['potion_small', 2]) }),
  }),
  side({
    id: 'sq_ew_herbs',
    name: 'Salve for the Wounded',
    level: 3,
    zone: 'emberwild',
    giver: 'quartermaster_odo',
    turnIn: 'quartermaster_odo',
    requires: ['mq_004'],
    steps: [step(undefined, collect('herb', 5, 'Gather Wild Herbs anywhere in the vale'))],
    offer: [
      'Half the outpost is nursing wolf bites and my salve jars are empty. Wild herbs grow all over the vale; look for the white flowers.',
      "Gather five and I'll brew enough salve for everyone, with a little left over for my profit margin. I mean, for you.",
    ],
    complete: [
      "Lovely, lovely. Smells like a meadow and tastes like regret. Perfect salve. Here's your share.",
    ],
    rewards: sideRewards(3, 0.3, { items: items(['potion_small', 4]) }),
  }),
  side({
    id: 'sq_ew_seeds',
    name: 'Glimmer in the Grass',
    level: 4,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'scout_lyra',
    requires: ['mq_004'],
    steps: [step(undefined, seeds(3, 'Find Glimmerseeds hidden around the vale', 'emberwild'))],
    offer: [
      'Have you seen them? Little sprouts of light in odd corners of the vale, like dandelions that swallowed a star. Glimmerseeds.',
      "They grow where shard-light soaked into the ground. They're shy: behind rocks, under bridges, on ledges nobody climbs.",
      "Find three and I'll show you where I've spotted the rest. I've been marking them on my map for weeks.",
    ],
    complete: [
      "Three! You have the eye for it. Here: I've marked every one I've seen on your map. Open it with M.",
      "Gather enough and they'll teach you things. Or they'll just be pretty. Either way.",
    ],
    rewards: sideRewards(4, 0.3, { flags: ['hint_ew_seeds'] }),
  }),
  side({
    id: 'sq_ew_heart',
    name: 'Heart of the Vale',
    level: 7,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'scout_lyra',
    requires: ['sq_ew_seeds'],
    steps: [
      step(
        undefined,
        interact(
          'ew_heart',
          'Claim the Heart of the Vale in the south-west forest',
          'emberwild',
          -88,
          88,
        ),
      ),
    ],
    offer: [
      "There's an old story about the vale's heart: a stone that beats in the far south-west corner, where the forest grows thickest.",
      "Hunters say it glows red at midsummer. I've never been brave enough to look. You, on the other hand, glow all the time.",
    ],
    complete: [
      "It was real? And it beat in your hand? ...Sky and stone. I will never call my mother's stories silly again.",
      "Here. It's only a card of me, but Mira in Havenreach says every face in her deck matters. Keep it close.",
    ],
    rewards: sideRewards(7, 0.3, { card: 'c_lyra' }),
  }),
  side({
    id: 'sq_ew_cart_1',
    name: 'The Ditched Cart',
    level: 4,
    zone: 'emberwild',
    giver: 'quartermaster_odo',
    turnIn: 'quartermaster_odo',
    requires: ['mq_005'],
    steps: [
      step(
        undefined,
        reach('emberwild', 40, 8, 9, "Find Odo's cart in the ditch by the east road"),
        kill('thornback_boar', 3, 'Chase off the boars eating the cabbages', 'emberwild', 70, 30),
      ),
    ],
    offer: [
      "Remember my cart? Still in a ditch by the east road, just past the fork. Full of things I'd very much like to sell.",
      'Find it, shoo off whatever is eating my cabbages, and tell me how bad the axle is.',
    ],
    complete: [
      'Snapped clean through? Of course it is. Nothing is ever just a bit broken in this vale. Right. Timber.',
    ],
    rewards: sideRewards(4, 0.25),
  }),
  side({
    id: 'sq_ew_cart_2',
    name: "An Axle's Worth of Timber",
    level: 5,
    zone: 'emberwild',
    giver: 'quartermaster_odo',
    turnIn: 'quartermaster_odo',
    requires: ['sq_ew_cart_1'],
    steps: [step(undefined, collect('timber', 3, 'Cut Timber from the fallen oaks of the vale'))],
    offer: [
      'I need three good lengths of timber for a new axle. The vale is full of fallen oaks; a strong arm and a sharp edge will do it.',
      "And before you ask: no, I can't carry them myself. I have a delicate back and a very busy counter.",
    ],
    complete: ["Oak! Proper oak. Give me an hour and a hammer, and she'll roll again."],
    rewards: sideRewards(5, 0.25),
  }),
  side({
    id: 'sq_ew_cart_3',
    name: 'Back on the Road',
    level: 5,
    zone: 'emberwild',
    giver: 'quartermaster_odo',
    turnIn: 'captain_brenna',
    requires: ['sq_ew_cart_2'],
    steps: [
      step(
        undefined,
        talk('captain_brenna', 'Tell Captain Brenna the supply cart is rolling again'),
      ),
    ],
    offer: [
      "She rolls! Go and tell the captain the supply line is open. She'll believe it from you. From me she'd want a receipt.",
    ],
    complete: [
      "The cart's back? Good. Supplies in, wounded out.",
      "You'd make a decent quartermaster if you ever get bored of hitting things. Don't tell Odo I said that.",
    ],
    rewards: sideRewards(5, 0.3, {
      bond: 15,
      items: items(['potion_small', 3], ['grenade_flame', 2]),
    }),
  }),
  side({
    id: 'sq_ew_brutes',
    name: 'Bigger They Come',
    level: 6,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'scout_lyra',
    requires: ['mq_007'],
    steps: [
      step(
        undefined,
        kill('grubling_brute', 2, 'Defeat Grubling Brutes in the warren', 'emberwild', -76, -36),
      ),
    ],
    offer: [
      'The warren grows a new brute every time we knock one down. They feed the biggest grublings shard-dust until they swell.',
      'Two more have crawled out since you were there. Put them down before they get ideas.',
    ],
    complete: ['Two fewer brutes. The warren will be squeaking about you for a generation.'],
    rewards: sideRewards(6, 0.3, { gear: 'rare' }),
  }),
  side({
    id: 'sq_ew_archers',
    name: 'Arrows from the Ridge',
    level: 9,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    requires: ['mq_009'],
    steps: [
      step(
        undefined,
        kill('bandit_archer', 5, 'Clear the archers from the northern ridge', 'emberwild', 44, -86),
      ),
    ],
    offer: [
      'The bandits we scattered left their archers on the ridge north of the hideout. They pick off anyone on the north road.',
      'Clear the ridge. Riders need that road.',
    ],
    complete: [
      'The north road is safe enough for riders again. My letters to Havenreach might actually arrive now.',
    ],
    rewards: sideRewards(9, 0.3, { items: items(['grenade_flash', 2]) }),
  }),
];

// ================================================================ SUNSCAR EXPANSE (Lv 13–19)
const SUNSCAR: QuestDef[] = [
  side({
    id: 'sq_ss_sage',
    name: 'Desert Sage',
    level: 13,
    zone: 'sunscar',
    giver: 'trader_farouk',
    turnIn: 'trader_farouk',
    requires: ['mq_019'],
    steps: [
      step(
        undefined,
        collect('herb', 4, 'Gather desert sage (Wild Herbs) near the oasis', 'sunscar', 2, 50, 32),
      ),
    ],
    offer: [
      'Friend! Your lips are cracked like old leather. Out here we chew desert sage against the sun; it grows in the scrub around the oasis.',
      "Bring me four sprigs and I'll brew salves for the whole camp. Fair prices are a kindness, and kindness is a trade too.",
    ],
    complete: [
      'Wonderful. The camp will smell of sage for a week, which is an improvement. Take these for the road.',
    ],
    rewards: sideRewards(13, 0.2, { items: items(['potion_major', 3]) }),
  }),
  side({
    id: 'sq_ss_stingers',
    name: 'Venom for Antidotes',
    level: 13,
    zone: 'sunscar',
    giver: 'trader_farouk',
    turnIn: 'trader_farouk',
    requires: ['mq_019'],
    steps: [
      step(
        undefined,
        kill(
          'sand_scorpion',
          10,
          'Harvest stingers from the scorpions south-west of camp',
          'sunscar',
          -72,
          34,
        ),
      ),
    ],
    offer: [
      'The scorpions south-west of camp carry the strongest venom in the Expanse, and the best antidotes are brewed from it.',
      'Ten stingers, please. Mind which end you hold.',
    ],
    complete: [
      'Ah, a fine harvest. Keep a panacea on your belt; the desert bites in more ways than one.',
    ],
    rewards: sideRewards(13, 0.2, { items: items(['potion_panacea', 3]) }),
  }),
  side({
    id: 'sq_ss_seeds',
    name: 'Seeds on the Wind',
    level: 14,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_021'],
    steps: [step(undefined, seeds(3, 'Find Glimmerseeds in the Expanse', 'sunscar'))],
    offer: [
      'The fallen stars left seeds of light in the sand, ember-child. The wind moves them, but never far.',
      'Find three, and I will read the rest from the wind for you. They glow brightest at the edges of things.',
    ],
    complete: [
      'Three. The wind has told me where the others sleep. I have marked them on your map; you will find them by their small voices.',
    ],
    rewards: sideRewards(14, 0.2, { flags: ['hint_ss_seeds'] }),
  }),
  side({
    id: 'sq_ss_heart',
    name: 'Heart of the Dunes',
    level: 18,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['sq_ss_seeds', 'mq_024'],
    steps: [
      step(
        undefined,
        interact(
          'ss_heart',
          'Claim the Heart of the Dunes in the south-east badlands',
          'sunscar',
          98,
          96,
        ),
      ),
    ],
    offer: [
      'In the far south-east badlands, where two pillars lean together like old lovers, the desert keeps its heart.',
      'Few have held it. Go and hold it.',
    ],
    complete: ['Now the dunes know your name. They will be gentler with you. A little.'],
    rewards: sideRewards(18, 0.2, { bond: 20 }),
  }),
  side({
    id: 'sq_ss_caravan_1',
    name: 'The Last Caravan',
    level: 15,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_022'],
    steps: [
      step(
        undefined,
        kill('dune_raider', 8, 'Defeat the raiders holding the last caravan', 'sunscar', -78, 90),
      ),
    ],
    offer: [
      "One of my caravans is still missing: my nephew's. The raiders who took it camp south, past the red tents, against the canyon wall.",
      "If he's alive, they'll have him. If he's not... bring me his compass. Either way, I need to know.",
    ],
    complete: [
      "Alive? He's alive! Tied to a cart wheel and furious, but alive. You've given my sister her son back, friend.",
    ],
    rewards: sideRewards(15, 0.2),
  }),
  side({
    id: 'sq_ss_caravan_2',
    name: 'Salvage from the Sands',
    level: 16,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'caravan_master_hadi',
    requires: ['sq_ss_caravan_1'],
    steps: [
      step(
        undefined,
        interact('ss_chest_oasis', 'Dig up the caravan strongbox by the oasis', 'sunscar', -16, 42),
      ),
    ],
    offer: [
      "My nephew says the raiders buried the caravan's strongbox by the oasis before they fled. West bank, under a dead palm.",
      "Our whole season's savings are in it. Please.",
    ],
    complete: [
      "The strongbox! Dented, sandy and still locked. Take a share. You've earned more, but this is what I have.",
    ],
    rewards: sideRewards(16, 0.2, { gear: 'rare' }),
  }),
  side({
    id: 'sq_ss_tithe',
    name: "The Queen's Tithe",
    level: 17,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_024'],
    steps: [
      step(
        undefined,
        interact(
          'ss_chest_colonnade',
          'Open the tithe cache among the fallen columns',
          'sunscar',
          15,
          -88,
        ),
        interact(
          'ss_vista_colonnade',
          'Look out over the desert from the colonnade',
          'sunscar',
          39,
          -86,
        ),
      ),
    ],
    offer: [
      "The Sandsworn paid a tithe of treasure to their queen's rest, sealed in a cache among the fallen columns.",
      'Open it, then climb to where the colonnade looks out over the desert. See what the queen saw. Some lessons are only learned from a height.',
    ],
    complete: [
      'The queen watched her kingdom from that height until the sand swallowed it. The Sandsworn were not cruel. Only proud.',
      'Pride builds tombs. Remember that, when the Watch tries to hand you a crown.',
    ],
    rewards: sideRewards(17, 0.2, { items: items(['book_ambusher', 1]) }),
  }),
  side({
    id: 'sq_ss_golems',
    name: 'Stone That Walks',
    level: 18,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_028'],
    steps: [
      step(
        undefined,
        kill('sand_golem', 4, 'Break the Sand Golems in the badlands', 'sunscar', 78, 44),
      ),
    ],
    offer: [
      'Sand golems wander out of the south-east badlands and flatten my waystations. Break four and the rest might take the hint.',
    ],
    complete: [
      'Four golems! My surveyors will build the next waystation out of the rubble. Waste nothing in the desert.',
    ],
    rewards: sideRewards(18, 0.2, { items: items(['bomb_destruction', 2]) }),
  }),
  side({
    id: 'sq_ss_rearguard',
    name: "The Queen's Rear Guard",
    level: 19,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_031'],
    steps: [
      step(
        undefined,
        kill(
          'sunscar_skeleton',
          10,
          'Grant rest to the rear guard in the north-east ruins',
          'sunscar',
          82,
          -84,
        ),
      ),
    ],
    offer: [
      "The queen's dead did not all go back to sleep. A rear guard still marches in the north-east ruins, waiting for orders that will never come.",
      'Grant them rest, as you did their queen.',
    ],
    complete: ['Rest, soldiers. The sand is quiet now. Thank you, ember-child.'],
    rewards: sideRewards(19, 0.2, { bond: 20 }),
  }),
];

// ================================================================ FROSTHOLLOW (Lv 22–27)
const FROSTHOLLOW: QuestDef[] = [
  side({
    id: 'sq_fh_pelts',
    name: 'Winter Pelts',
    level: 22,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'hunter_bjorn',
    requires: ['mq_033'],
    steps: [
      step(
        undefined,
        kill('frost_wolf', 8, 'Hunt Frost Wolves south of the pass', 'frosthollow', -42, 72),
      ),
    ],
    offer: [
      'Abbey needs pelts. Novices are freezing. Wolves south of the pass have thick coats.',
      'Eight.',
    ],
    complete: [
      'Good pelts. Clean. Novices will stop shivering through vespers. Abbess will stop glaring.',
    ],
    rewards: sideRewards(22, 0.15, { items: items(['potion_major', 3]) }),
  }),
  side({
    id: 'sq_fh_timber',
    name: 'Fuel for the Abbey Fires',
    level: 22,
    zone: 'frosthollow',
    giver: 'trader_olga',
    turnIn: 'trader_olga',
    requires: ['mq_033'],
    steps: [step(undefined, collect('timber', 4, 'Cut Timber in the pine woods'))],
    offer: [
      'Welcome, love, welcome. You look frozen through. Here, hold this cup while I tell you my troubles.',
      "The abbey's woodpile is down to kindling and that bell keeps us up all night. Four good logs from the pine woods and I'll keep a hot draught waiting for you.",
    ],
    complete: [
      'Bless you. The hearth will roar tonight. Warm hands fight better, I always say. Mostly because I say it to everyone.',
    ],
    rewards: sideRewards(22, 0.15, { items: items(['campfire_kit', 2]) }),
  }),
  side({
    id: 'sq_fh_fish',
    name: 'Beneath the Ice',
    level: 24,
    zone: 'frosthollow',
    giver: 'trader_olga',
    turnIn: 'trader_olga',
    requires: ['sq_fh_timber'],
    steps: [
      step(
        undefined,
        collect('fish', 3, 'Fish through the ice at the frozen lake', 'frosthollow', 22, -16, 30),
      ),
    ],
    offer: [
      "The lake's frozen, but the fish haven't noticed. Cut a hole at the shore and bring me three.",
      "I'll make a stew the novices will remember in their prayers.",
    ],
    complete: ["Look at them! Silver as coins. Stew tonight, and you're first in line."],
    rewards: sideRewards(24, 0.15, { items: items(['potion_elixir', 2]) }),
  }),
  side({
    id: 'sq_fh_seeds',
    name: 'Seeds Under Snow',
    level: 22,
    zone: 'frosthollow',
    giver: 'trader_olga',
    turnIn: 'trader_olga',
    requires: ['mq_033'],
    steps: [step(undefined, seeds(3, 'Find Glimmerseeds poking through the snow', 'frosthollow'))],
    offer: [
      'You know those little glowing sprouts? They poke up through the snow here, bright as candles. My granddaughter collects them.',
      "Find three and I'll show you her map. She's very proud of it.",
    ],
    complete: [
      "Three! Here's her map; I've copied it onto yours. She drew hearts on the good ones. I didn't have the heart to stop her.",
    ],
    rewards: sideRewards(22, 0.15, { flags: ['hint_fh_seeds'] }),
  }),
  side({
    id: 'sq_fh_sexton',
    name: "The Sexton's Box",
    level: 23,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'abbess_vira',
    requires: ['mq_035'],
    steps: [
      step(
        undefined,
        interact(
          'fh_chest_graves',
          "Recover the sexton's iron box from the graveyard",
          'frosthollow',
          -7,
          44,
        ),
      ),
    ],
    offer: [
      "Our sexton kept the graveyard's records in an iron box by the east wall. When the dead rose, he ran and left it behind.",
      'The name of every soul buried there is inside. Bring it back.',
    ],
    complete: [
      'Every name, still legible. We will bury each of them again, properly, when this is over. Thank you.',
    ],
    rewards: sideRewards(23, 0.15, { bond: 20 }),
  }),
  side({
    id: 'sq_fh_offerings',
    name: 'Offerings Beneath the Ice',
    level: 25,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'abbess_vira',
    requires: ['mq_036'],
    steps: [
      step(
        undefined,
        interact(
          'fh_chest_chapel',
          "Recover the drowned chapel's offering chest",
          'frosthollow',
          14,
          -56,
        ),
      ),
    ],
    offer: [
      "The drowned chapel's offering chest survived the sinking. Pilgrims left their gifts to the bell there for a century.",
      'Recover it before the wraiths take an interest.',
    ],
    complete: [
      "Pilgrims' coins and prayer beads. We will use them to rebuild. Take a share; the pilgrims would want their gifts to help the living.",
    ],
    rewards: sideRewards(25, 0.15, { gear: 'rare' }),
  }),
  side({
    id: 'sq_fh_heart',
    name: 'Heart of the Frost',
    level: 26,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'abbess_vira',
    requires: ['mq_036'],
    steps: [
      step(
        undefined,
        interact(
          'fh_heart',
          'Claim the Heart of the Frost in the far north-west',
          'frosthollow',
          -92,
          -88,
        ),
      ),
    ],
    offer: [
      'The oldest chronicle of this house speaks of a heart of living ice in the far north-west, west of the overlook, where the pines end.',
      'Brother Hesk visited it once. He said it was warm.',
    ],
    complete: ['Warm, as he said. Some things the cold cannot take. I find that a comfort.'],
    rewards: sideRewards(26, 0.15, { bond: 20 }),
  }),
  side({
    id: 'sq_fh_hunt_1',
    name: 'The Last Hunt: Tracks',
    level: 26,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'hunter_bjorn',
    requires: ['mq_037'],
    steps: [
      step(
        undefined,
        kill('frost_wolf', 6, 'Hunt the great pack in the eastern pines', 'frosthollow', 88, 72),
      ),
    ],
    offer: [
      'Last hunt before the snows close the east woods. Big pack, deep in the pines by the ridge.',
      'Come.',
    ],
    complete: ['Good. Now the real prey. Yetis. Their hoard is up in the highlands.'],
    rewards: sideRewards(26, 0.15),
  }),
  side({
    id: 'sq_fh_hunt_2',
    name: 'The Last Hunt: The Hoard',
    level: 27,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'trader_olga',
    requires: ['sq_fh_hunt_1'],
    steps: [
      step(
        undefined,
        kill('frost_yeti', 2, 'Defeat the yetis guarding the hoard', 'frosthollow', 100, -44),
        interact('fh_chest_yeti', "Open the yetis' hoard", 'frosthollow', 104, -52),
      ),
    ],
    offer: [
      "Yetis hoard what they steal. Traders' packs. Pilgrims' boots.",
      "Break two, open the hoard, take what you find to Olga. She'll know whose it was.",
    ],
    complete: [
      "My brother's pack! He lost it on the high road three winters back. Oh, the old fool will cry.",
      "Thank you, love. Thank Bjorn too. He'd never let me.",
    ],
    rewards: sideRewards(27, 0.15, { items: items(['potion_elixir', 3], ['grenade_whirl', 2]) }),
  }),
];

// ================================================================ HAVENREACH (system guides)
const HAVENREACH: QuestDef[] = [
  side({
    id: 'sq_hr_brew',
    name: "Tessa's Tonic",
    level: 11,
    zone: 'havenreach',
    giver: 'merchant_tessa',
    turnIn: 'merchant_tessa',
    requires: ['mq_014'],
    steps: [step(undefined, collect('herb', 6, 'Gather Wild Herbs (Emberwild is full of them)'))],
    offer: [
      "Oh, a new face! You look like someone who gets hurt professionally. I brew the draughts in this city, and I'm out of wild herbs.",
      "Six, please? Emberwild's meadows are full of them.",
    ],
    complete: [
      "Perfect! Fresh as morning. Take a few draughts off the top, with a brewer's thanks. Come back when you're bleeding. I mean, thirsty.",
    ],
    rewards: sideRewards(11, 0.2, { items: items(['potion_major', 4]) }),
  }),
  side({
    id: 'sq_hr_bonds',
    name: 'A Song for a Friend',
    level: 11,
    zone: 'havenreach',
    giver: 'minstrel_lio',
    turnIn: 'minstrel_lio',
    requires: ['mq_014'],
    steps: [
      step(
        undefined,
        flag('tut_bond', 'Build a bond with someone you trust (talk to them, then choose Bond)'),
      ),
    ],
    offer: [
      'A traveller with a story in their eyes! Everyone needs friends, Shardbearer, even people who glow.',
      'Spend a moment with someone: a song, a word, a quiet meal. Captain Brenna, the High Warden, whoever you trust. Then come and tell me about it.',
    ],
    complete: [
      'A bond! I can hear it in your step. Friendship is the only thing that grows when you give it away.',
      'Here, a verse for the road: four glimmer-sprouts hide in this city, one in each far corner. I marked them for you. Do not tell the pigeons.',
    ],
    rewards: sideRewards(11, 0.2, { bond: 25, flags: ['hint_hr_seeds'] }),
  }),
  side({
    id: 'sq_hr_bounty',
    name: 'A Name on the Board',
    level: 12,
    zone: 'havenreach',
    giver: 'bounty_master_quill',
    turnIn: 'bounty_master_quill',
    requires: ['mq_017'],
    steps: [step(undefined, flag('tut_bounty', 'Complete a bounty from the Bounty Board'))],
    offer: [
      'You met me for five seconds and left. Rude, but efficient. I like efficient.',
      'The board posts fresh bounties every dawn: pick one, do the work, claim the coin. Finish one, and I put your name on the board for good.',
    ],
    complete: [
      "There. Your name's on the board. Contracts at dawn, coin at dusk. Don't make me cross you off.",
    ],
    rewards: sideRewards(12, 0.2, { items: items(['bounty_seal', 3]) }),
  }),
  side({
    id: 'sq_hr_cards',
    name: 'Faces of the Isles',
    level: 12,
    zone: 'havenreach',
    giver: 'card_collector_mira',
    turnIn: 'card_collector_mira',
    requires: ['mq_014'],
    steps: [
      step(undefined, flag('tut_card_slotted', 'Place a relic card in your deck (Cards, L)')),
    ],
    offer: [
      'Is that a Captain Brenna card in your pack? Do you know how rare she is? She never sits for portraits!',
      'Cards are not for pockets, they are for decks. Slot her into your deck and feel what she lends you. Then come back and tell me everything.',
    ],
    complete: [
      "Doesn't it feel like she's standing right behind you? Collect whole sets and the bonds between the faces grow stronger.",
      'Take a spare from my own deck. And this little gem fell out of a card pack; Ysa will know what to do with it.',
    ],
    rewards: sideRewards(12, 0.2, { card: 'c_lio', items: items(['gem_dmg_1', 1]) }),
  }),
  side({
    id: 'sq_hr_gems',
    name: 'Facets of Light',
    level: 13,
    zone: 'havenreach',
    giver: 'gemcutter_ysa',
    turnIn: 'gemcutter_ysa',
    requires: ['sq_hr_cards'],
    steps: [step(undefined, flag('tut_gem_socketed', 'Socket a gem into one of your skills'))],
    offer: [
      'Mira says you have a gem now. Hold it to the lantern... see how the facets drink the glow?',
      'Set it in a skill and the skill will drink it too. Ember gems burn hotter; tide gems turn faster. Try it.',
    ],
    complete: [
      'Oh, it suits you. The light runs cleaner through it. Bring me three alike someday and I will fuse them into something worthy.',
    ],
    rewards: sideRewards(13, 0.2, { items: items(['gem_cd_1', 1]) }),
  }),
  side({
    id: 'sq_hr_sigils',
    name: 'Ink and Promise',
    level: 23,
    zone: 'havenreach',
    giver: 'sigil_scribe_orin',
    turnIn: 'sigil_scribe_orin',
    requires: ['mq_035'],
    steps: [step(undefined, flag('tut_sigil_read', 'Read a sigil codex from your bag'))],
    offer: [
      'The abbess sent you home with a codex, did she not? I can smell the ink from here. Frosthollow binds its books with pine resin.',
      'A sigil is a promise inked in light. Read the codex and the promise becomes yours; set the sigil active, and it will keep it.',
    ],
    complete: [
      "Feel that? The ink remembers you now. Gather more codices; the more you learn, the deeper a sigil's promise runs.",
    ],
    rewards: sideRewards(23, 0.2, { items: items(['book_pressure_points', 1]) }),
  }),
];

// ================================================================ THE ISLES (Lv 16–17)
const ISLES: QuestDef[] = [
  side({
    id: 'sq_is_glimmer',
    name: 'The Glimmering Isle',
    level: 16,
    zone: 'glimmer',
    giver: 'harbormaster_sol',
    turnIn: 'harbormaster_sol',
    requires: ['mq_026'],
    steps: [
      step(undefined, arrive('glimmer', 'Sail east to Glimmer Isle')),
      step(
        'Sprouts of light glitter across the island. Old ruins stand on the southern hill.',
        interact('gl_lore_ruins', 'Read the inscription in the southern ruins', 'glimmer', 3, -67),
      ),
    ],
    offer: [
      "Seen the green glow on the horizon, east of the shipping lane? That's Glimmer Isle. Sprouts of light all over it, thick as clover.",
      "There are old ruins on the south hill with writing nobody's read in a century. Sail over, read it, and tell me what it says. I'll trade you my chart of the island's glimmer-patches.",
    ],
    complete: [
      "'Where the light falls, let it grow.' Glimmerbloom, eh? Here's my chart: every patch I've spotted from the rigging.",
      "Mind the wolves over there. They're friendlier than they look. No, they're not.",
    ],
    rewards: sideRewards(16, 0.2, { flags: ['hint_gl_seeds'] }),
  }),
  side({
    id: 'sq_is_haven',
    name: 'Workshop Fires',
    level: 16,
    zone: 'haven',
    giver: 'steward_pim',
    turnIn: 'steward_pim',
    requires: ['mq_026'],
    steps: [
      step(
        undefined,
        reach('haven', -24.5, -6.5, 5, 'Light the forge in the workshop'),
        reach('haven', 22.5, -8, 5, 'Dust off the research table'),
        reach('haven', 7.5, 30.5, 5, 'Inspect the dispatch dock'),
      ),
    ],
    offer: [
      "Before we build anything, the place needs to feel lived in. Would you walk the stations with me? Well, ahead of me. I'll catch up.",
      "Light the workshop forge, dust off the research table and check the dispatch dock. Then we're open for business!",
    ],
    complete: [
      'Open for business! Research, crafting, crews: it all starts now. Come back often. I get lonely. And the garden needs weeding.',
    ],
    rewards: sideRewards(16, 0.2, { items: items(['fusion_alloy', 2]) }),
  }),
];

export const SIDE_QUESTS: QuestDef[] = [
  ...EMBERWILD,
  ...HAVENREACH,
  ...SUNSCAR,
  ...ISLES,
  ...FROSTHOLLOW,
];
